/**
 * Rail de navigation — tablette (U6b vague 9, planche NavigationTablette, 768 px).
 *
 * À 768 px la barre latérale dépliée mangeait un tiers de l'écran. Le rail garde une colonne
 * d'icônes (Accueil, entrées directes, les groupes du niveau, Réglages) ; un groupe ouvre le
 * PANNEAU modal « Navigation du … » (la barre latérale elle-même, en Sheet), ce groupe déplié.
 * - chaque icône a un nom accessible complet, groupe + étapes (A11Y-08) :
 *   « Vérifier — étape : Tests et qualité » ;
 * - le groupe de l'écran courant porte `aria-current="true"`, l'entrée courante `"page"` ;
 * - cibles de 44 × 44 ; à la fermeture du panneau, Radix rend le focus au bouton du rail.
 */
import type { JSX } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { useSidebar } from '@/components/ui/sidebar'
import {
  ACCUEIL, currentNavItem, itemsDuGroupePourMode, niveauParId, premierNiveau, sectionDuChemin,
  type NavGroup, type Niveau,
} from '@/lib/nav'
import type { NavigationMode } from '@/lib/navigationMode'
import { useT } from '@/i18n/language'

const CIBLE = 'flex size-11 shrink-0 items-center justify-center rounded-md text-muted-foreground outline-none '
  + 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring '
  + 'aria-[current=page]:bg-sidebar-accent aria-[current=page]:text-sidebar-accent-foreground '
  + 'aria-[current=true]:bg-sidebar-accent aria-[current=true]:text-sidebar-accent-foreground'

/** Étapes d'un groupe, sans leur numéro : « ① Cadrage » → « Cadrage ». */
export function etapesDuGroupe(groupe: NavGroup, roles: string[], mode: NavigationMode): string[] {
  const vues = new Set<string>()
  for (const item of itemsDuGroupePourMode(groupe, roles, mode)) {
    if (item.etape) vues.add(item.etape.replace(/^[^\p{L}]+/u, '').trim())
  }
  return [...vues]
}

export function RailTablette({ roles, mode, niveau, groupeOuvert, onGroupe }: {
  roles: string[]
  /** Groupe déplié dans le panneau ouvert depuis le rail. */
  groupeOuvert?: string | undefined
  mode: NavigationMode
  niveau: Niveau
  /** Groupe à déplier dans le panneau qui va s'ouvrir. */
  onGroupe: (id: string) => void
}): JSX.Element {
  const { t } = useT()
  const { pathname, search } = useLocation()
  const { openMobile, setOpenMobile } = useSidebar()
  const actif = currentNavItem(pathname, search)
  const groupeCourant = sectionDuChemin(pathname, search)
  const niv = niveauParId(niveau)

  const ouvrir = (id: string): void => {
    onGroupe(id)
    setOpenMobile(true)
  }

  return (
    <nav aria-label={t(`nav.niveau.${niveau}`, niv.navLabel)} data-testid="rail-tablette"
         className="sticky top-0 z-20 flex h-svh w-16 shrink-0 flex-col items-center gap-1 border-r bg-sidebar py-2">
      <Link to={ACCUEIL.path} aria-label={t(`nav.${ACCUEIL.path}`, ACCUEIL.label)} title={ACCUEIL.label}
            aria-current={actif === ACCUEIL ? 'page' : undefined} data-testid="rail-accueil" className={CIBLE}>
        <ACCUEIL.icon className="size-5" aria-hidden="true" />
      </Link>
      {premierNiveau(niveau, roles, mode).map((groupe) => {
        const libelle = t(`grp.${groupe.label}`, groupe.label)
        if (groupe.type === 'entree') {
          const item = itemsDuGroupePourMode(groupe, roles, mode)[0]
          if (!item) return null
          return (
            <Link key={groupe.id} to={item.path} aria-label={t(`nav.${item.path}`, item.label)} title={item.label}
                  aria-current={actif === item ? 'page' : undefined} data-testid={`rail-${groupe.id}`} className={CIBLE}>
              <groupe.icon className="size-5" aria-hidden="true" />
            </Link>
          )
        }
        const etapes = etapesDuGroupe(groupe, roles, mode)
        const nom = etapes.length === 0 ? libelle
          : `${libelle} — ${etapes.length > 1 ? t('rail.etapes', 'étapes') : t('rail.etape', 'étape')} : ${etapes.join(', ')}`
        return (
          <button key={groupe.id} type="button" aria-label={nom} title={libelle} data-testid={`rail-${groupe.id}`}
                  aria-haspopup="dialog" aria-expanded={openMobile && groupeOuvert === groupe.id}
                  aria-current={groupeCourant === groupe.id ? 'true' : undefined}
                  onClick={() => ouvrir(groupe.id)} className={CIBLE}>
            <groupe.icon className="size-5" aria-hidden="true" />
          </button>
        )
      })}
    </nav>
  )
}
